import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type ColumnDef,
} from '@tanstack/react-table'
import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { BoolCell, DateCell, ListCell, MutedCell, NameCell } from '@/components/TableCells'
import { Site } from '@/services/sites/sitesApi'
import { CountryFlag } from '../data/countries.tsx'
import { countryName } from './CountryFilter'

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  country: 'country',
  earningsAccountName: 'earningsAccount.name',
  objectState: 'objectState',
  politicalAdvertising: 'politicalAdvertising',
  paywalled: 'paywalled',
  requestedInLastDay: 'requestedInLastDay',
  updateTime: 'updateTime',
  createTime: 'createTime',
}

/** The advertiser-access option each `filterTeamsType` value is labelled with on the edit page. */
const FILTER_TYPE_LABELS: Record<string, string> = {
  INHERITED: 'sites.inheritedFromPublisher',
  NONE: 'sites.onlyAllowSelected',
  EXCLUDES: 'sites.allExceptSelected',
}

interface SiteTableProps {
  sites: Site[]
  loading?: boolean
  emptyMessage?: string
  showEarningsAccount?: boolean
  selection?: Site[]
  onSelectionChange?: (sites: Site[]) => void
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function SiteTable({
  sites,
  loading,
  emptyMessage,
  showEarningsAccount,
  selection,
  onSelectionChange,
  sort,
  onSortChange,
}: SiteTableProps) {
  const { t } = useTranslation()
  const selectable = !!onSelectionChange

  const columns = useMemo<ColumnDef<Site, unknown>[]>(() => {
    const cols: ColumnDef<Site, unknown>[] = []

    if (selectable) {
      cols.push(selectionColumn<Site>())
    }

    cols.push(
      {
        accessorKey: 'name',
        header: () => (
          <span>{t('common.name')}</span>
        ),
        meta: { minWidth: 200, label: t('common.name') },
        cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
      },
      {
        accessorKey: 'country',
        header: t('common.country'),
        meta: { minWidth: 140, label: t('common.country') },
        enableSorting: true,
        cell: ({ row }) =>
          row.original.country ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
              <CountryFlag code={row.original.country} />
              {countryName(row.original.country)}
            </span>
          ) : (
            <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>—</span>
          ),
      },
    )

    if (showEarningsAccount) {
      cols.push({
        accessorKey: 'earningsAccountName',
        header: t('sites.publisher'),
        meta: { minWidth: 140 },
        cell: ({ row }) => (
          <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
            {row.original.earningsAccountName || '—'}
          </span>
        ),
      })
    }

    cols.push({
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => (
        <span
          className={`chip chip--${row.original.objectState === 'ACTIVE' ? 'running' : 'draft'}`}
          style={{ fontSize: 11 }}
        >
          <span className="dot" />
          {row.original.objectState}
        </span>
      ),
    })

    cols.push(
      optionalColumn<Site>({
        accessorKey: 'ownerTeamName',
        header: t('common.team'),
        meta: { minWidth: 150 },
        cell: ({ row }) => <MutedCell value={row.original.ownerTeamName} />,
      }),
      optionalColumn<Site>({
        accessorKey: 'politicalAdvertising',
        header: t('sites.politicalAds'),
        size: 150,
        cell: ({ row }) => <MutedCell value={row.original.politicalAdvertising} />,
      }),
      optionalColumn<Site>({
        id: 'filterTeamsType',
        header: t('sites.filterType'),
        enableSorting: false,
        meta: { minWidth: 190 },
        cell: ({ row }) => {
          const key = FILTER_TYPE_LABELS[row.original.filterTeamsType]
          return <MutedCell value={key && t(key)} />
        },
      }),
      optionalColumn<Site>({
        accessorKey: 'paywalled',
        header: t('common.paywalled'),
        size: 110,
        cell: ({ row }) => <BoolCell value={row.original.paywalled} />,
      }),
      optionalColumn<Site>({
        accessorKey: 'requestedInLastDay',
        header: t('adUnits.requested'),
        size: 110,
        cell: ({ row }) => <BoolCell value={row.original.requestedInLastDay} />,
      }),
      optionalColumn<Site>({
        id: 'iabCategories',
        header: t('blocklists.iabCategories'),
        enableSorting: false,
        meta: { minWidth: 160 },
        cell: ({ row }) => <ListCell values={row.original.iabCategories} />,
      }),
      optionalColumn<Site>({
        id: 'labels',
        header: t('common.labels'),
        enableSorting: false,
        meta: { minWidth: 150 },
        cell: ({ row }) => <ListCell values={row.original.labels} />,
      }),
      optionalColumn<Site>({
        id: 'siteRuleset',
        header: t('publishers.inventoryRuleset'),
        enableSorting: false,
        meta: { minWidth: 150 },
        cell: ({ row }) => <MutedCell value={row.original.siteRuleset?.name} />,
      }),
      optionalColumn<Site>({
        accessorKey: 'updateTime',
        header: t('common.updated'),
        size: 120,
        cell: ({ row }) => <DateCell value={row.original.updateTime} />,
      }),
      optionalColumn<Site>({
        accessorKey: 'createTime',
        header: t('common.created'),
        size: 120,
        cell: ({ row }) => <DateCell value={row.original.createTime} />,
      }),
    )

    return cols
  }, [selectable, showEarningsAccount, t])

  return (
    <DataTable
      columns={columns}
      data={sites}
      loading={loading}
      emptyMessage={emptyMessage ?? 'No sites found.'}
      columnsKey="sites"
      rowHref={(row) => `/publishing/sites/${row.id}`}
      {...(selectable ? { selection, onSelectionChange } : {})}
      serverSort={
        onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
      }
    />
  )
}
